import { get, toInteger, toString } from 'lodash';
import { HttpService } from 'app/services';

/**
 * Helpers for FD+ on-demand dispense sync job status.
 *
 * - Normalizes API status strings for UI (RunDispenseSyncButton).
 * - Polls GET /api/background-jobs/dispense-sync/{jobId}/status on an interval until terminal.
 * - One active poll per job id (module-level map); restarting replaces the previous poll for that id.
 */

// Matches Hangfire projection values returned by BackgroundJobController.
export const DISPENSE_SYNC_JOB_STATUS = {
  QUEUED: 'Queued',
  PROCESSING: 'Processing',
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
};

// Unknown / empty values are treated as Queued so the UI keeps showing progress.
export const normalizeDispenseSyncJobStatus = (value) => {
  const status = toString(value).trim();
  if (!status) return '';
  if (status === DISPENSE_SYNC_JOB_STATUS.PROCESSING) return DISPENSE_SYNC_JOB_STATUS.PROCESSING;
  if (status === DISPENSE_SYNC_JOB_STATUS.SUCCEEDED) return DISPENSE_SYNC_JOB_STATUS.SUCCEEDED;
  if (status === DISPENSE_SYNC_JOB_STATUS.FAILED) return DISPENSE_SYNC_JOB_STATUS.FAILED;
  return DISPENSE_SYNC_JOB_STATUS.QUEUED;
};

// Queued and Processing both show the spinner in the table cell.
export const isDispenseSyncRunningStatus = (status) =>
  [DISPENSE_SYNC_JOB_STATUS.QUEUED, DISPENSE_SYNC_JOB_STATUS.PROCESSING].includes(
    normalizeDispenseSyncJobStatus(status),
  );

// Same value as RunDispenseSyncButton getLoopbackDaysFromMonths(12).
export const FULL_SYNC_LOOPBACK_DAYS = 365;

// When the job reached Succeeded/Failed (for last-run caption). Supports camelCase and PascalCase.
export const getDispenseSyncCompletionTime = (response) =>
  get(response, 'data.stateChangedAt', get(response, 'Data.StateChangedAt', get(response, 'data.StateChangedAt')));

// Script lookback window used for that run (Period line in caption).
export const getDispenseSyncLoopbackDays = (response) =>
  get(
    response,
    'data.loopbackDays',
    get(response, 'Data.LoopbackDays', get(response, 'data.LoopbackDays', get(response, 'Data.loopbackDays'))),
  );

// Human-readable period for i18n (e.g. "1 month", "12 months", "Full").
export const formatDispenseSyncRangeLabel = (loopbackDays, t) => {
  const days = toInteger(loopbackDays);
  if (!days) {
    return '';
  }

  if (days === FULL_SYNC_LOOPBACK_DAYS) {
    return t('patientGroup:RUN_DISPENSE_SYNC_RANGE_FULL');
  }

  const months = days / 30;
  if (months === 1) {
    return t('patientGroup:RUN_DISPENSE_SYNC_RANGE_ONE_MONTH');
  }

  return t('patientGroup:RUN_DISPENSE_SYNC_RANGE_N_MONTHS', { count: months });
};

/** @type {Map<string, { stop: () => void }>} */
// Shared across all RunDispenseSyncButton instances in the table.
const activePollsByJobId = new Map();

export const isDispenseSyncJobPolling = (jobId) => {
  const normalized = toString(jobId).trim();
  return Boolean(normalized && activePollsByJobId.has(normalized));
};

// Alias used in poll callbacks to ignore events after stop/replace.
export const isActiveDispenseSyncJob = (jobId) => isDispenseSyncJobPolling(jobId);

/** Stops every in-flight poll (e.g. patient group update flow). */
export const stopDispenseSyncJobPolling = () => {
  activePollsByJobId.forEach((entry) => {
    if (typeof entry.stop === 'function') {
      entry.stop();
    }
  });
  activePollsByJobId.clear();
};

/**
 * Poll job status until Succeeded or Failed.
 *
 * @returns {() => void} Call to cancel timers and remove this poll from the map.
 * Fires onStatusChange on every non-terminal tick; onCompleted/onFailed once, then stops.
 * Network errors stop polling and invoke onError (if provided).
 */
export const startDispenseSyncJobPolling = ({
  jobId,
  intervalMs = 5000,
  onStatusChange,
  onCompleted,
  onFailed,
  onError,
}) => {
  const normalizedJobId = toString(jobId).trim();
  if (!normalizedJobId) {
    return () => {};
  }

  // Same job id started again (e.g. effect re-run): cancel the old interval first.
  const existing = activePollsByJobId.get(normalizedJobId);
  if (existing && typeof existing.stop === 'function') {
    existing.stop();
  }

  let isStopped = false;
  let timerId = null;

  const stop = () => {
    isStopped = true;
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
    const current = activePollsByJobId.get(normalizedJobId);
    if (current && current.stop === stop) {
      activePollsByJobId.delete(normalizedJobId);
    }
  };

  activePollsByJobId.set(normalizedJobId, { stop });

  const scheduleNext = () => {
    if (isStopped) {
      return;
    }

    timerId = setTimeout(tick, intervalMs);
  };

  // Skip callbacks after stop() so a slow HTTP response cannot update the wrong UI pass.
  const invokeIfCurrent = (callback, ...args) => {
    if (isStopped) {
      return;
    }
    if (typeof callback === 'function') {
      callback(...args);
    }
  };

  const tick = async () => {
    try {
      const response = await HttpService.get(`/api/background-jobs/dispense-sync/${normalizedJobId}/status`);
      if (isStopped) {
        return;
      }

      const rawStatus = get(
        response,
        'data.status',
        get(response, 'Data.Status', get(response, 'status', get(response, 'Status'))),
      );
      const status = normalizeDispenseSyncJobStatus(rawStatus);

      invokeIfCurrent(onStatusChange, status, response);

      if (status === DISPENSE_SYNC_JOB_STATUS.SUCCEEDED) {
        const shouldNotify = !isStopped;
        stop();
        // Terminal: notify after stop so isDispenseSyncJobPolling is false for other logic.
        if (shouldNotify && typeof onCompleted === 'function') {
          onCompleted(response);
        }
        return;
      }

      if (status === DISPENSE_SYNC_JOB_STATUS.FAILED) {
        const shouldNotify = !isStopped;
        stop();
        if (shouldNotify && typeof onFailed === 'function') {
          onFailed(response);
        }
        return;
      }
    } catch (error) {
      stop();
      invokeIfCurrent(onError, error);
      return;
    }

    scheduleNext();
  };

  // First request runs immediately; later ticks wait intervalMs.
  tick();
  return stop;
};
